import { Suspense, lazy } from 'react';
import { Link, Navigate, Outlet, useLocation, useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Spinner, makeStyles } from '@fluentui/react-components';
import { AppShell, ShellIcons, isActiveRoute, tokens, type ShellLinkProps, type ShellNavItem, shellMedia } from '@vigeo/ui';
import { isEmployed, personDisplayName } from '@vigeo/shared';
import { fetchPerson, fetchReviewQueue } from '@/lib/api';
import { LOGOUT_URL, fetchAuthState } from '@/lib/auth';
import { fixtureMode } from '@/lib/data-mode';
import { buildReleaseLabel, isProductionCopy } from '@/lib/release-label';
import { ROSTER_PATH, SECTION_KEYS, enabledSections, pathAvailable, sectionOfPath, shellCaption, type SectionKey } from '@/lib/sections';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { SectionUnavailablePage } from '@/pages/SectionUnavailablePage';
import { SignInPage } from '@/pages/SignInPage';

// Reached only behind `fixtureMode`, which is the literal `false` in a production build, so
// this function and its import are dead code there and the bar is never emitted.
let lazyDesignQaBar: ReturnType<typeof lazy<typeof import('@/features/design-qa').DesignQaBar>> | null = null;
function designQaBar() {
  lazyDesignQaBar ??= lazy(() => import('@/features/design-qa').then((m) => ({ default: m.DesignQaBar })));
  return lazyDesignQaBar;
}

const useStyles = makeStyles({
  centered: { display: 'flex', justifyContent: 'center', paddingBlock: tokens.spacingVerticalXXXL },
  // On a phone the crumb is a full-height target (Gate 6: 44×44); desktop keeps the caption height.
  crumb: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, [shellMedia.belowTablet]: { minHeight: '2.75rem' } },
  current: { color: tokens.colorNeutralForeground1, fontWeight: tokens.fontWeightSemibold, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
});

/** The router's link in the shape the shell expects. */
function RouterLink({ to, children, ...rest }: ShellLinkProps) {
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
}

/**
 * Staff navigation, from frame 06b 0.1's sidebar: Dashboard · Hiring (Candidates, Offers, Interviews &
 * orientation, Portal links) · Roster · Review · Forms. /app/design stays routable for the style guide
 * but is not offered here.
 *
 * "Interviews & orientation" and "Portal links" arrived with their pages (#802), on the bounded
 * cross-person reads #303 added (`GET /api/interviews`, `GET /api/portal-links`). They are Hiring
 * pages, so a Roster-only build shows neither: the section rule below hides them with the rest of Hiring.
 *
 * "Candidates" points at the People directory, which currently lists EVERYONE including staff. The
 * frame splits that in two — Candidates for the pipeline, Roster for people we employ — and Roster
 * is now the employed half. Narrowing this half to the pipeline changes a page HR uses today, so it
 * belongs with row 0.1, where the five hiring stages are defined.
 */
/** A destination, or a disclosure holding them. `active` is derived per render, so it is not here. */
type Destination = Omit<ShellNavItem, 'active' | 'badge' | 'children' | 'unavailable'> & {
  /** The section this item belongs to (#610). Typed, so a menu item cannot be added without one. */
  readonly section: SectionKey;
  readonly children?: readonly Omit<ShellNavItem, 'active' | 'badge' | 'children' | 'unavailable' | 'group'>[];
};

/**
 * The destination whose badge carries the review count. Named here rather than compared inline, so
 * renaming that key is a compile error instead of a badge that silently stops appearing — which is
 * what the explicit `Destination` type cost us when it widened `key` from a literal to `string`.
 */
const REVIEW_KEY = 'review';

/**
 * The menu's groups (#742, designer 404's drawing N3 on #715): People for hiring and staff, Paperwork for what HR checks.
 * The shell draws a heading only where it tells groups apart, so a Roster-only menu has none.
 */
const PEOPLE = 'People';
const PAPERWORK = 'Paperwork';

const DESTINATIONS: readonly Destination[] = [
  { key: 'dashboard', section: 'dashboard', label: 'Dashboard', to: '/app', icon: 'dashboard' },
  {
    key: 'hiring',
    section: 'hiring',
    label: 'Hiring',
    to: '/app/people',
    icon: 'briefcase',
    group: PEOPLE,
    // Their icons show only on the icon rail, where the group is flattened: drawing N4's briefcase and page.
    children: [
      { key: 'candidates', label: 'Candidates', to: '/app/people', icon: 'briefcase' },
      { key: 'offers', label: 'Offers', to: '/app/offers', icon: 'document' },
      { key: 'interviews', label: 'Interviews & orientation', to: '/app/interviews', icon: 'people' },
      { key: 'portal-links', label: 'Portal links', to: '/app/portal-links', icon: 'externalLink' },
    ],
  },
  { key: 'roster', section: 'roster', label: 'Roster', to: '/app/roster', icon: 'people', group: PEOPLE },
  { key: 'review', section: 'review', label: 'Review', to: '/app/review', icon: 'review', group: PAPERWORK },
  { key: 'forms', section: 'forms', label: 'Forms', to: '/app/forms', icon: 'forms', group: PAPERWORK },
];

/** Every destination that is actually a page, groups flattened into their children. */
const LEAF_DESTINATIONS = DESTINATIONS.flatMap((d) => d.children ?? [d]);

/**
 * What the address says about the person on it. One reading for the breadcrumb and the side menu, so the two cannot
 * disagree about whether this is a candidate or an employee (#721). `enabled` is false where no crumb is drawn, so a
 * switched-off page asks the server for nothing.
 */
function useRoutePerson(enabled: boolean) {
  /**
   * `/app/people/new` matches `/app/people/:id` with `id: 'new'`, so this has to be checked FIRST
   * and has to suppress the person branch. Without it the shell claimed the user was on a person
   * record and fired `GET /api/people/new`, which 404s on every visit to Add candidate.
   */
  const addCandidate = useMatch('/app/people/new');
  const maybePerson = useMatch('/app/people/:id');
  /**
   * The offer screens (#261, #262) are person-scoped routes one segment deeper, and the frames
   * put the same "Candidates › <person>" above them.
   *
   * Spelled out to four segments rather than widened to `/app/people/:id/*`. A splat would match
   * `/app/people/new` too — the very bug the branch above exists to fix — and a pattern that
   * cannot match a one-segment sibling cannot reintroduce it. It is still taken after
   * `addCandidate`, so the two rules compose rather than race.
   */
  const onOfferSend = useMatch('/app/people/:id/offer/send');
  /** Review carries the record id too, so it is one segment longer and needs its own pattern. */
  const onOfferReview = useMatch('/app/people/:id/offer/:recordId/review');
  const onOfferStep = onOfferSend ?? onOfferReview;
  /**
   * `new` is excluded from the offer branch as well as from the record branch.
   *
   * `/app/people/new/offer/send` is not a real page, but `useMatch` will still read `new` as an
   * id and this crumb would fire `GET /api/people/new` for it — the same 404 the Add-candidate
   * branch above exists to stop. Excluding the id rather than the exact path closes it for every
   * depth at once.
   */
  /** The employee profile (#585) sits under the Roster, and its crumb reads "Roster › <person>". */
  const onProfile = useMatch('/app/roster/:id');
  const person = addCandidate || onOfferStep?.params.id === 'new' ? null : maybePerson ?? onOfferStep ?? onProfile;
  // Shares the person page's query key, so this costs no extra request.
  const record = useQuery({
    queryKey: ['person', person?.params.id ?? ''],
    queryFn: () => fetchPerson(person?.params.id ?? ''),
    enabled: enabled && person !== null && person.params.id !== undefined && person.params.id !== '',
  });
  /** Whether this address is a staff member's record: their Roster profile, or their record page once it has loaded as an employee. */
  const employee = onProfile !== null || (record.data !== undefined && isEmployed(record.data.lifecycle));
  return { addCandidate, person, onProfile, record, employee };
}

function Breadcrumb({ pathname }: { pathname: string }) {
  const styles = useStyles();
  const { addCandidate, person, record, employee: underRoster } = useRoutePerson(true);
  if (addCandidate) {
    return (
      <>
        <Link to="/app/people" className={styles.crumb}>
          <ShellIcons.chevronLeft />
          {LEAF_DESTINATIONS.find((d) => d.to === '/app/people')?.label ?? 'Candidates'}
        </Link>
        <ShellIcons.chevronRight />
        <span aria-current="page" className={styles.current}>Add candidate</span>
      </>
    );
  }
  if (person) {
    // An employee's record reads "Roster › <person>" wherever it is opened from (#721); until the record has loaded,
    // the crumb keeps the route's own reading.
    return (
      <>
        {/* Named from the destination, not hardcoded: the label lives in one place, so this cannot
            drift from the sidebar again. The frames' own breadcrumb reads "Candidates › <person>". */}
        <Link to={underRoster ? '/app/roster' : '/app/people'} className={styles.crumb}>
          <ShellIcons.chevronLeft />
          {underRoster ? LEAF_DESTINATIONS.find((d) => d.to === '/app/roster')?.label ?? 'Roster' : LEAF_DESTINATIONS.find((d) => d.to === '/app/people')?.label ?? 'Candidates'}
        </Link>
        <ShellIcons.chevronRight />
        <span aria-current="page" className={styles.current}>
          {record.data ? personDisplayName(record.data.name) : 'Person'}
        </span>
      </>
    );
  }
  // LEAVES, not DESTINATIONS: a group's `to` is a placeholder the nav never follows, and searching
  // the groups would name the disclosure ("Hiring") where the page is ("Candidates").
  const here = LEAF_DESTINATIONS.find((d) => isActiveRoute(pathname, d.to));
  return <span aria-current="page" className={styles.current}>{here?.label ?? ''}</span>;
}

export function AppLayout() {
  const styles = useStyles();
  const { pathname } = useLocation();
  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const user = auth.data?.status === 'signed-in' ? auth.data.user : null;
  const signedIn = user !== null;
  // Both roles may read the queue (SWA allows HR_Viewer on /api/*); only Admin can decide.
  // Which sections this build has switched on (#610). Every one of them, unless the build was told otherwise.
  const sections = enabledSections();
  const available = pathAvailable(pathname, sections);
  // The count is for the Review item's badge, so it is not asked for when Review is switched off.
  const queue = useQuery({ queryKey: ['review'], queryFn: () => fetchReviewQueue(), enabled: signedIn && sections.has('review') });

  const waiting = queue.data?.items.length ?? 0;
  // An employee's record page is the Roster's, whichever address it was opened at (#721), so the menu lights Roster.
  const { employee: onEmployee } = useRoutePerson(signedIn && available);
  const menuPath = onEmployee ? ROSTER_PATH : pathname;
  // A group's own row is a disclosure, never a destination, so it is not marked active — its
  // children carry that. Marking both would light two rows for one page.
  // A switched-off section is not in the menu at any width, with no greyed "Coming soon" row (#734, which
  // replaces the greyed rows of #610 and the narrower hiding of #634). The pages stay refused by address above.
  const nav: readonly ShellNavItem[] = DESTINATIONS.filter(({ section }) => sections.has(section)).map(({ children, section: _section, ...d }) => ({
    ...d,
    active: children === undefined && isActiveRoute(menuPath, d.to),
    badge: d.key === REVIEW_KEY && waiting > 0 ? { count: waiting, label: `${waiting} waiting` } : undefined,
    ...(children === undefined ? {} : { children: children.map((child) => ({ ...child, active: isActiveRoute(menuPath, child.to) })) }),
  }));

  const account = user
    ? {
        displayName: user.displayName,
        roleLabel: user.isAdmin ? 'Admin' : 'Viewer',
        signOutHref: LOGOUT_URL,
      }
    : undefined;

  // Which batch of the test site, or of staging, this is (#693, ADR-024). Nothing unless the release named
  // one, and on a Roster-only build only when it was built as staging's, so production never has it.
  // Read here, not at load, like the sections.
  const buildLabel = buildReleaseLabel({
    label: import.meta.env.VITE_RELEASE_LABEL,
    commit: import.meta.env.VITE_RELEASE_COMMIT,
    allSectionsOn: SECTION_KEYS.every((key) => sections.has(key)),
    productionCopy: isProductionCopy(import.meta.env.VITE_PRODUCTION_COPY),
  });

  const DesignQaBar = fixtureMode ? designQaBar() : null;
  return (
    <AppShell
      nav={nav}
      Link={RouterLink}
      currentPath={pathname}
      account={account}
      buildLabel={buildLabel ?? undefined}
      caption={shellCaption(sections)}
      // No crumb over a switched-off page: it would name the page, link into its section, and read the person in the address.
      breadcrumb={signedIn && available ? <nav aria-label="Breadcrumb" style={{ display: 'contents' }}><Breadcrumb pathname={pathname} /></nav> : null}
    >
      {auth.isPending ? (
        <div className={styles.centered}>
          <Spinner label="Checking your access" />
        </div>
      ) : auth.data?.status === 'signed-out' || auth.isError ? (
        <SignInPage />
      ) : auth.data?.status === 'no-access' ? (
        <NoAccessPage />
      ) : !available && sectionOfPath(pathname) === 'dashboard' ? (
        // The start page is the Dashboard; with it switched off, the app starts on the Roster.
        <Navigate to={ROSTER_PATH} replace />
      ) : !available ? (
        // Instead of the page, which is therefore never loaded and asks the server for nothing.
        <SectionUnavailablePage />
      ) : (
        <>
          {DesignQaBar !== null ? (
            <Suspense fallback={null}>
              <DesignQaBar />
            </Suspense>
          ) : null}
          <Outlet />
        </>
      )}
    </AppShell>
  );
}
